import { useCallback } from "react";

import { ConfigFileStatus, ConfigFileStatusType } from "../../../entities/ConfigFileStatus";
import { useTerminalContext } from "../contexts/TerminalContext";
import { configApi } from "../services/configApi";
import { createWithLoading, wrapError } from "../utils/configApiHelpers";
import { useConfigState } from "./useConfigState";

/**
 * Merges storage place data from backend into config places.
 * 
 * @param from - Array of storage places from backend with current state
 * @param to - Object map of places from config file (keyed by "areaCode-row-placeNumber-level")
 * 
 * Example key format: "A-1-5-2" means Area A, Row 1, Place 5, Level 2
 */
const mergeNewToOldPlaces = (from: any, to: any) => {
  // Create mapping: address -> place object reference
  // Using object reference as value eliminates repeated dictionary lookups
  const addressMap: Record<string, any> = {};

  // Key format is "areaCode-row-placeNumber-level" (e.g., "A-1-5-2")
  // This composite key uniquely identifies each storage place in the warehouse
  Object.entries(to).forEach(([key, placeObj]: [string, any]) => {
    addressMap[placeObj.address] = placeObj;
  });

  // Merge backend data into config places
  from.forEach(src => {
    const place = addressMap[src.address];
    if (place) {
      // Update place with current backend state
      place.id = src.id;
      place.old_address = src.old_address;
      place.rack_address = src.rack_address;
      place.place_type = src.place_type;
      place.is_preparation = src.is_preparation;
      place.is_for_splitted_pallets = src.is_for_splitted_pallets;
      place.lock_datetime_from = src.lock_datetime_from;
      place.lock_datetime_to = src.lock_datetime_to;
      place.comment = src.comment;
      place.occupied = (src.state.occupied ?? 0) > 0;
      place.storage_entity_ids = src.state.storage_entity_ids ?? [];
      place.blocked = src.state.blocked ?? false;
      place.company_id = src.app_data.company_id ?? null;
      place.customer_name = src.app_data.customer_name ?? null;
      place.pallet_company_id = src.app_data.pallet_company_id ?? null;
      place.pallet_customer_name = src.app_data.pallet_customer_name ?? null;
      place.pallet_product_number = src.app_data.pallet_product_number ?? null;
      place.pallet_product_name = src.app_data.pallet_product_name ?? null;
      place.pallet_is_t1 = src.app_data.pallet_is_t1 ?? false;
      place.product_article_id = src.app_data.product_article_id ?? null;
      place.product_name = src.app_data.product_name ?? null;
    }
  });
};

/**
 * Merges backend port data into existing port config objects (in-place mutation).
 *
 * @param from - Array of ports from backend (contains id, disabled, etc.)
 * @param to - Array of ports from config (polygon, styling, blocked flag)
 *
 * This function does NOT return anything. It mutates objects in `to`.
 */
export const mergeNewToOldPorts = (from: any[], to: any[]) => {
  // Build lookup map: name -> backend port reference
  const nameMap: Record<string, any> = {};

  from.forEach(src => {
    nameMap[src.name] = src;
  });

  // Mutate old objects (exactly like mergeNewToOldPlaces)
  to.forEach(port => {
    const match = nameMap[port.name];
    if (!match) return;

    // Update only required fields
    port.id = match.id;
    port.blocked = match.disabled ?? false;
    port.lock_datetime_from = match.lock_datetime_from;
    port.lock_datetime_to = match.lock_datetime_to;
    port.comment = match.comment;
  });
};

/**
 * Hook for loading config files
 */
export const useConfigFileLoader = () => {
  const { setFileList, setError, setIsLoading } = useTerminalContext();
  const { clearConfigState, setConfigState, throwNoConfigFound } = useConfigState();

  const loadConfigFile = useCallback(
    async (
      terminalId: number,
      src: string,
      status: ConfigFileStatusType | null,
      signal?: AbortSignal
    ) => {
      const config = await configApi.fetchReadyConfigFileContent(terminalId, src, signal);
      const storagePlaces = await configApi.fetchStoragePlaces(terminalId, signal);
      const buildingPorts = await configApi.fetchBuildingPorts(terminalId, signal);

      mergeNewToOldPlaces(storagePlaces, config.places);
      mergeNewToOldPorts(buildingPorts, config.portBoundaries);

      setConfigState(config, status, src);
    },
    [setConfigState]
  );

  const loadConfigFromBackend = useCallback(
    async (terminalId: number, signal?: AbortSignal) => {
      setError(null);
      const withLoading = createWithLoading(setIsLoading);
      return withLoading(async () => {
        try {
          const configFiles = await configApi.fetchConfigFiles(terminalId, undefined, signal);
          setFileList(configFiles);

          if (!configFiles || configFiles.length === 0) {
            throwNoConfigFound();
          }

          // Prefer newest production, then newest of any other status
          const sortedConfigs = [...configFiles].sort(
            (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
          );
          const productionConfigs = sortedConfigs.filter(
            (c) => c.status === ConfigFileStatus.production
          );
          const nonProductionConfigs = sortedConfigs.filter(
            (c) => c.status !== ConfigFileStatus.production
          );
          const candidates = [...productionConfigs, ...nonProductionConfigs];

          let lastError: Error | null = null;
          for (const candidate of candidates) {
            try {
              await loadConfigFile(terminalId, candidate.src, candidate.status, signal);
              return candidate;
            } catch (error: any) {
              console.warn(`Failed to load config "${candidate.src}" (status ${candidate.status}):`, error);
              lastError = error;
            }
          }

          clearConfigState();
          throw lastError ?? new Error("No usable config files found");
        } catch (error: any) {
          console.error("Failed to load config from backend:", error);
          if (error.message !== "NO_CONFIG_FOUND") {
            clearConfigState();
          }
          throw error.message === "NO_CONFIG_FOUND"
            ? error
            : wrapError("Failed to load configuration from the server", error);
        }
      });
    },
    [loadConfigFile, clearConfigState, throwNoConfigFound, setError, setFileList, setIsLoading]
  );

  return {
    loadConfigFile,
    loadConfigFromBackend,
  };
};
